import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Reports.css";
import { apiFetch } from "../utils/api";

const monthLabels = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const Reports = () => {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [stockReport, setStockReport] = useState(null);
  const [monthlySales, setMonthlySales] = useState([]);
  const [memberSales, setMemberSales] = useState([]);
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const [supplierPerformance, setSupplierPerformance] = useState([]);
  const [expenseBreakdown, setExpenseBreakdown] = useState({ totals: null, items: [] });

  const loadReports = useCallback(selectedYear => {
    const branchQuery = branchFilter ? `&branchPublicId=${encodeURIComponent(branchFilter)}` : "";
    const fromDate = `${selectedYear}-01-01`;
    const toDate = `${selectedYear}-12-31`;

    apiFetch(`/api/reports/stock?year=${selectedYear}${branchQuery}`)
      .then(res => res.json())
      .then(setStockReport)
      .catch(err => console.error("Failed to load stock report", err));

    apiFetch(`/api/reports/monthly-sales?year=${selectedYear}${branchQuery}`)
      .then(res => res.json())
      .then(data => setMonthlySales(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load monthly sales", err));

    apiFetch(`/api/reports/member-sales?year=${selectedYear}${branchQuery}`)
      .then(res => res.json())
      .then(data => setMemberSales(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load member sales", err));

    apiFetch(`/api/reports/team-performance?year=${selectedYear}${branchQuery}`)
      .then(res => res.json())
      .then(data => setTeamPerformance(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load team performance", err));

    apiFetch(`/api/purchase/reports/summary?fromDate=${fromDate}&toDate=${toDate}`)
      .then(res => res.json())
      .then(data => setPurchaseSummary(data))
      .catch(err => console.error("Failed to load purchase summary", err));

    apiFetch(`/api/purchase/reports/supplier-performance?fromDate=${fromDate}&toDate=${toDate}`)
      .then(res => res.json())
      .then(data => setSupplierPerformance(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load supplier performance", err));

    apiFetch(`/api/purchase/reports/expense-breakdown?fromDate=${fromDate}&toDate=${toDate}`)
      .then(res => res.json())
      .then(data => setExpenseBreakdown(data && typeof data === "object" ? data : { totals: null, items: [] }))
      .catch(err => console.error("Failed to load expense breakdown", err));
  }, [branchFilter]);

  useEffect(() => {
    apiFetch("/api/branches")
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setBranches(list);
        if (list.length === 1 && list[0]?.publicId) {
          setBranchFilter(String(list[0].publicId));
        }
      })
      .catch(err => {
        console.error("Failed to load branches", err);
        setBranches([]);
      });
  }, []);

  useEffect(() => {
    loadReports(year);
  }, [year, loadReports]);

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return [current - 2, current - 1, current, current + 1].map(String);
  }, []);

  const monthlyTotals = useMemo(() => {
    const map = new Map(monthlySales.map(m => [Number(m.month), m]));
    return monthLabels.map((label, idx) => {
      const rec = map.get(idx + 1);
      return {
        label,
        totalAmount: Number(rec?.totalAmount || 0),
        invoiceCount: Number(rec?.invoiceCount || 0)
      };
    });
  }, [monthlySales]);

  return (
    <div className="reports-page page main-content">
      <div className="reports-header">
        <div>
          <h1>Reports</h1>
          <p>Review consolidated sales and stock analytics for operational decisions.</p>
        </div>
        <div className="reports-header-actions">
          <label htmlFor="reports-branch-select">Branch</label>
          <select id="reports-branch-select" value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
            {branches.length !== 1 && <option value="">All branches</option>}
            {branches.map(branch => (
              <option key={branch.publicId || branch.id} value={branch.publicId || ""}>
                {`${branch.branchCode || ""} - ${branch.branchName || "Branch"}`}
              </option>
            ))}
          </select>
          <label htmlFor="year-select">Year</label>
          <select id="year-select" value={year} onChange={e => setYear(e.target.value)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <section className="report-section">
        <h2>Stock Management Report</h2>
        <div className="kpi-grid">
          <div className="kpi-card"><span>Total Products</span><strong>{stockReport?.summary?.totalProducts || 0}</strong></div>
          <div className="kpi-card"><span>Low Stock</span><strong>{stockReport?.summary?.lowStock || 0}</strong></div>
          <div className="kpi-card"><span>Expiring Soon</span><strong>{stockReport?.summary?.expiringSoon || 0}</strong></div>
          <div className="kpi-card"><span>Expired</span><strong>{stockReport?.summary?.expired || 0}</strong></div>
          <div className="kpi-card"><span>Estimated Stock Value</span><strong>{`AED ${Number(stockReport?.summary?.stockValue || 0).toFixed(2)}`}</strong></div>
        </div>

        <div className="report-table-wrap">
          <table className="report-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Type</th>
                <th className="num-cell">Stock</th>
                <th className="num-cell">Reorder</th>
                <th>Expiry</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(stockReport?.items || []).length === 0 ? (
                <tr><td colSpan="6">No inventory records.</td></tr>
              ) : (
                (stockReport?.items || []).map(item => (
                  <tr key={item.id}>
                    <td data-label="Product">{item.productName}</td>
                    <td data-label="Type">{item.productType || "-"}</td>
                    <td data-label="Stock" className="num-cell">{`${Number(item.stockQty || 0)} ${item.unit || "pcs"}`}</td>
                    <td data-label="Reorder" className="num-cell">{item.reorderLevel || 0}</td>
                    <td data-label="Expiry">{item.expiryDate || "-"}</td>
                    <td data-label="Status">{item.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="report-section">
        <h2>Month-wise Sales</h2>
        <div className="report-table-wrap">
          <table className="report-table">
            <thead>
              <tr>
                <th>Month</th>
                <th className="num-cell">Invoices</th>
                <th className="num-cell">Total Sales (AED)</th>
              </tr>
            </thead>
            <tbody>
              {monthlyTotals.map(row => (
                <tr key={row.label}>
                  <td data-label="Month">{row.label}</td>
                  <td data-label="Invoices" className="num-cell">{row.invoiceCount}</td>
                  <td data-label="Total Sales (AED)" className="num-cell">{row.totalAmount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="report-section two-col-grid">
        <div>
          <h2>Sales By Members</h2>
          <div className="report-table-wrap">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th className="num-cell">Services Done</th>
                  <th className="num-cell">Sales (AED)</th>
                </tr>
              </thead>
              <tbody>
                {memberSales.length === 0 ? (
                  <tr><td colSpan="3">No member sales data.</td></tr>
                ) : (
                  memberSales.map(member => (
                    <tr key={member.memberName}>
                      <td data-label="Member">{member.memberName}</td>
                      <td data-label="Services Done" className="num-cell">{member.servicesDone}</td>
                      <td data-label="Sales (AED)" className="num-cell">{Number(member.salesAmount || 0).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2>Team Performance</h2>
          <div className="report-table-wrap">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th className="num-cell">Contribution %</th>
                  <th className="num-cell">Avg Ticket (AED)</th>
                </tr>
              </thead>
              <tbody>
                {teamPerformance.length === 0 ? (
                  <tr><td colSpan="3">No team performance data.</td></tr>
                ) : (
                  teamPerformance.map(member => (
                    <tr key={`${member.memberName}-perf`}>
                      <td data-label="Member">{member.memberName}</td>
                      <td data-label="Contribution %" className="num-cell">{Number(member.contributionPercent || 0).toFixed(2)}%</td>
                      <td data-label="Avg Ticket (AED)" className="num-cell">{Number(member.avgTicket || 0).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="report-section">
        <h2>Purchase And Non Inventory Report</h2>
        <div className="kpi-grid purchase-kpi-grid">
          <div className="kpi-card"><span>Purchase Invoices</span><strong>{purchaseSummary?.summary?.invoiceCount || 0}</strong></div>
          <div className="kpi-card"><span>Purchase Total</span><strong>{`AED ${Number(purchaseSummary?.summary?.totalAmount || 0).toFixed(2)}`}</strong></div>
          <div className="kpi-card"><span>Purchase VAT</span><strong>{`AED ${Number(purchaseSummary?.summary?.vatAmount || 0).toFixed(2)}`}</strong></div>
          <div className="kpi-card"><span>Purchase Grand Total</span><strong>{`AED ${Number(purchaseSummary?.summary?.grandTotal || 0).toFixed(2)}`}</strong></div>
          <div className="kpi-card"><span>Expense Total</span><strong>{`AED ${Number(expenseBreakdown?.totals?.total || 0).toFixed(2)}`}</strong></div>
        </div>

        <div className="two-col-grid">
          <div>
            <h3>Supplier Performance</h3>
            <div className="report-table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th className="num-cell">Invoices</th>
                    <th className="num-cell">Grand Total (AED)</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierPerformance.length === 0 ? (
                    <tr><td colSpan="3">No supplier performance data.</td></tr>
                  ) : supplierPerformance.map(row => (
                    <tr key={row.supplier_id}>
                      <td data-label="Supplier">{row.supplier_name || "-"}</td>
                      <td data-label="Invoices" className="num-cell">{Number(row.invoice_count || 0)}</td>
                      <td data-label="Grand Total (AED)" className="num-cell">{Number(row.grand_total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3>Expense Breakdown</h3>
            <div className="report-table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th className="num-cell">Count</th>
                    <th className="num-cell">Total (AED)</th>
                  </tr>
                </thead>
                <tbody>
                  {(expenseBreakdown?.items || []).length === 0 ? (
                    <tr><td colSpan="3">No expense breakdown data.</td></tr>
                  ) : (expenseBreakdown?.items || []).map(row => (
                    <tr key={row.expense_type}>
                      <td data-label="Type">{row.expense_type_name || row.expense_type}</td>
                      <td data-label="Count" className="num-cell">{Number(row.expense_count || 0)}</td>
                      <td data-label="Total (AED)" className="num-cell">{Number(row.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reports;
